import type { FC, ReactElement, ReactNode } from "react";
import { useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dayjs } from "dayjs";
import isFunction from "lodash/isFunction";
import uniq from "lodash/uniq";

import { colorsPalettes } from "@similarweb/styles";
import { Badge, CircularLoader, DropdownContent } from "@similarweb/ui-components";
import { Text, Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { getIsInDiscount } from "app/components/discountCampaign/DiscountBanner/utils/helpers";
import { PulseDot } from "app/components/pulse-dot/PulseDot";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { AddOnType, historyHookPreselect } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { useTotalVisitsComponentHistoryHook } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook";
import { openHooksModalV4 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import {
    hasAddonsEnabled,
    isTraditionalTrialOrAfterTrialUser,
    isTraditionalTrialUser,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { withAutoReposition } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/steps/withAutoReposition";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { Presets } from "../../types";
import { GranularitySwitch } from "../GranularitySwitch";
import { MoreDataBanner } from "../MoreDataBanner";

import * as SC from "./StyledComponents";

const CUSTOM_DURATION_ITEM_ID = "duration::custom";

const PILL_TEXT = {
    addonUpgrade: "duration_selector.locked.addon.upgrade",
    contactUs: "duration_selector.locked.contact.us",
    upgrade: "duration_selector.locked.upgrade",
    unlock: "duration_selector.locked.unlock",
} as const;

interface IDropdownStep {
    closePopup?: VoidFunction;
    onCustomDuration: VoidFunction;
    onPresetSelect: (id: string) => void;
    presets: Presets;
    selectedPreset: string | null;
    selectedCustomDuration: any | null;
    disableCalendar: boolean;
    showWeeklyToggle?: boolean;
    isWeeklyDataAvailable?: boolean;
    weeklyToggleProps?: {
        weekly?: ReactNode;
        monthly?: ReactNode;
    };
    onGranularityToggleClick?: (overrideTrackingKey: string) => void;
}
const DURATION_6_MONTH = "6m";
const DURATION_12_MONTH = "12m";
const DURATION_15_MONTH = "15m";
const HISTORICAL_DATA_ADDON_DURATIONS = [DURATION_6_MONTH, DURATION_12_MONTH, DURATION_15_MONTH];

const extractMonthsCount = (duration: string): string => duration.replaceAll(/[^0-9]+/g, "");

const getPackagePagePreset = (presetId: string): string => {
    if (presetId === DURATION_6_MONTH && isTraditionalTrialUser(swSettings)) {
        return presetId;
    }

    const isHistoricalDataAddon = HISTORICAL_DATA_ADDON_DURATIONS.includes(presetId);

    return isHistoricalDataAddon ? AddOnType.HistoricalData : presetId;
};

const isNewPurchaseWizardEnabled = (): boolean =>
    GrowthbookService.growthbookInstance.isOn(TestsNames.NEW_PURCHASE_WIZARD) &&
    isTraditionalTrialOrAfterTrialUser(swSettings);

const DropdownStep: FC<IDropdownStep> = ({
    closePopup,
    onCustomDuration,
    onPresetSelect,
    presets,
    selectedPreset,
    selectedCustomDuration,
    disableCalendar,
    showWeeklyToggle,
    isWeeklyDataAvailable,
    weeklyToggleProps,
    onGranularityToggleClick,
    ...props
}) => {
    const { getAddon, getIsAddonAvailable, getIsAddonUpgradable, isAddonsDataLoading } = useAddonsState();
    const translate = useTranslation();
    const isDiscount = getIsInDiscount({
        discountEndTime: swSettings.user?.customData?.DiscountEndDate,
    });
    const isSalesApp = proNavigator.isSalesSolution();
    const isPulsingIndicatorEnabled = GrowthbookService.growthbookInstance.isOn(TestsNames.TOTAL_VISITS_HOOK);
    const { isPulsarShown: isMajorPeek } = useTotalVisitsComponentHistoryHook();
    const {
        state: { name: pageName, parent },
    } = useCurrentStateAndParams();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const handlePopupClose = useCallback(() => {
        if (isFunction(closePopup)) {
            closePopup();
        }
    }, [closePopup]);

    const redirectToAddonPackagePage = (month: string) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `add-on/Date range/${month}`);
        handlePurchaseAddonClick(historyHookPreselect, Number(extractMonthsCount(month)));
    };

    const redirectToPackagePage = (presetId: string) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `date range/${presetId}/package_page`);

        const monthsCount = presetId.replaceAll(/[^0-9]+/g, "");
        const packageId = isNewPurchaseWizardEnabled ? `${AddOnType.HistoricalData}${monthsCount}` : undefined;

        goToPackagesPage({
            ...(isSalesApp ? { hook: "HistoricalData" } : { presetId: getPackagePagePreset(presetId), packageId }),
            ...(isDiscount ? { promo: "esrim", discountBanner: true } : {}),
        });
    };

    const handleLockedPreset = (presetId: string) => {
        handlePopupClose();
        const isAddOnAvailable = HISTORICAL_DATA_ADDON_DURATIONS.includes(presetId);
        const canPurchaseHistoricalData = getIsAddonAvailable(AddOnType.HistoricalData);
        const canPurchaseAddons = hasAddonsEnabled(swSettings);

        if (isTraditionalTrialOrAfterTrialUser(swSettings) && canPurchaseHistoricalData) {
            redirectToPackagePage(presetId);
            return;
        }

        if (!canPurchaseHistoricalData || (isAddOnAvailable && !canPurchaseAddons)) {
            openHooksModalV4("allowedDurationDate");
            return;
        }

        redirectToAddonPackagePage(presetId);
    };

    const handleUnlockedPreset = (itemId: string) => {
        if (itemId !== CUSTOM_DURATION_ITEM_ID) {
            handlePopupClose();
            onPresetSelect(itemId);
        } else {
            onCustomDuration();
        }
    };

    const handleDropdownItemClick = (item: ReactElement<unknown>) => {
        const id = (item.props as { id?: string }).id;
        const preset = presets.find((set) => set.id === id);

        if (preset?.locked) {
            if (isAddonsDataLoading) {
                return;
            }
            handleLockedPreset(preset.id);
        } else if (id) {
            handleUnlockedPreset(id);
        }
    };

    const getLockedPillText = (id: string): string => {
        const canPurchaseHistoricalData = getIsAddonAvailable(AddOnType.HistoricalData);
        const canUpgradeHistoricalData = getIsAddonUpgradable(AddOnType.HistoricalData);
        const hasHistoryAddon = !!getAddon(AddOnType.HistoricalData);

        if (isTraditionalTrialOrAfterTrialUser(swSettings) && canPurchaseHistoricalData) {
            return PILL_TEXT.unlock;
        }

        if (
            !hasAddonsEnabled(swSettings) ||
            !canPurchaseHistoricalData ||
            (hasHistoryAddon && !canUpgradeHistoricalData)
        ) {
            return PILL_TEXT.contactUs;
        }

        if (hasHistoryAddon && canUpgradeHistoricalData) {
            return PILL_TEXT.addonUpgrade;
        }

        if (
            !hasAddonsEnabled(swSettings) ||
            !canPurchaseHistoricalData ||
            (hasHistoryAddon && !canUpgradeHistoricalData)
        ) {
            return PILL_TEXT.contactUs;
        }

        if (hasHistoryAddon && canUpgradeHistoricalData) {
            return PILL_TEXT.addonUpgrade;
        }

        const isAddonDuration = HISTORICAL_DATA_ADDON_DURATIONS.includes(id);
        if (!isAddonDuration) {
            return PILL_TEXT.upgrade;
        }

        if (!isSalesApp) {
            return PILL_TEXT.unlock;
        }

        return PILL_TEXT.upgrade;
    };

    const renderLockedPill = (id: string): ReactNode => {
        if (isAddonsDataLoading) {
            return (
                <CircularLoader
                    options={{
                        svg: { stroke: colorsPalettes.midnight[50], strokeWidth: "2", r: 8, cx: "50%", cy: "50%" },
                        style: { height: 18, width: 18 },
                    }}
                />
            );
        }

        const lockedPillText = getLockedPillText(id);
        if (lockedPillText === PILL_TEXT.unlock) {
            return (
                <Badge bgColor={colorsPalettes.blue[100]}>
                    <Text
                        fontVariant={"caption2"}
                        color={colorsPalettes.blue[400]}
                    >
                        {translate(PILL_TEXT.unlock)}
                    </Text>
                </Badge>
            );
        }

        return (
            <Badge
                type="custom"
                bgColor={colorsPalettes.indigo[300]}
            >
                {translate(lockedPillText)}
            </Badge>
        );
    };

    const isPulseVisible = (id: string): boolean => {
        const WEBSITE_ANALYSIS = "digitalsuite_website";
        const PAGES_TO_SHOW_PULSE_INDICATOR = [
            "digitalsuite_website_trafficandengagement",
            "digitalsuite_website_websiteperformance",
        ];
        const isAddOnExist = HISTORICAL_DATA_ADDON_DURATIONS.includes(id);
        const isAddOnAvailable = getIsAddonAvailable(AddOnType.HistoricalData);
        const isCurrentPageAllowsToShowPulse =
            !!pageName && parent === WEBSITE_ANALYSIS && PAGES_TO_SHOW_PULSE_INDICATOR.includes(pageName);

        return isAddOnExist && isCurrentPageAllowsToShowPulse && isAddOnAvailable && isMajorPeek && !isSalesApp;
    };

    const formatItemDate = (from: Dayjs, to: Dayjs) =>
        `(${uniq([from, to].map((date) => date.format("MMM YYYY"))).join(" - ")})`;

    return (
        <div>
            {showWeeklyToggle && !isWeeklyDataAvailable ? (
                <SC.GranularitySwitchContainer>
                    <GranularitySwitch
                        onGranularityToggleClick={() => onGranularityToggleClick?.("WeeklyKeywords/datepicker")}
                        weeklyToggleProps={weeklyToggleProps}
                    />
                </SC.GranularitySwitchContainer>
            ) : null}
            <DropdownContent
                {...props}
                selectedIds={{ [selectedPreset || CUSTOM_DURATION_ITEM_ID]: true }}
                onClick={handleDropdownItemClick}
            >
                {presets.map(({ id, text, value: { from, to }, locked }) => {
                    if (locked) {
                        return (
                            <SC.DropdownItem
                                className="DropdownItemStep"
                                id={id}
                                key={id}
                            >
                                <Flex alignItems="center">
                                    {text}
                                    {isPulsingIndicatorEnabled && isPulseVisible(id) ? (
                                        <PulseDot
                                            pulseDiameter={10}
                                            padding={4}
                                            centerDiameter={10}
                                            color={colorsPalettes.blue[400]}
                                        />
                                    ) : null}
                                </Flex>
                                <SC.LockedItemPillStyled>{renderLockedPill(id)}</SC.LockedItemPillStyled>
                            </SC.DropdownItem>
                        );
                    } else {
                        const secondaryText = id.match(/\d+d/) ? "" : formatItemDate(from, to);
                        return (
                            <SC.DropdownItem
                                className="DropdownItemStep"
                                id={id}
                                key={id}
                            >
                                {text}
                                {secondaryText ? <SC.DropdownItemDate>{secondaryText}</SC.DropdownItemDate> : null}
                            </SC.DropdownItem>
                        );
                    }
                })}
                {disableCalendar ? (
                    <></>
                ) : (
                    <SC.DropdownItem
                        id={CUSTOM_DURATION_ITEM_ID}
                        className="DropdownItemStep"
                    >
                        {translate("duration_selector.custom")}
                        {selectedCustomDuration ? " " : "..."}
                        {selectedCustomDuration ? (
                            <SC.DropdownItemDate>
                                {formatItemDate(selectedCustomDuration.from, selectedCustomDuration.to)}
                            </SC.DropdownItemDate>
                        ) : null}
                    </SC.DropdownItem>
                )}
            </DropdownContent>
            <SC.BottomSection>
                <MoreDataBanner onClick={handlePopupClose} />
            </SC.BottomSection>
        </div>
    );
};

export default withAutoReposition(DropdownStep);
